import WalletLink from "walletlink";
import WalletConnect from "@walletconnect/client";
import QRCodeModal from "@walletconnect/qrcode-modal";
import Vue from "vue";

const ethers = require("ethers");

const STATE = {
  LOADING: "loading",
  REQUESTING_WALLET: "requesting_wallet",
  LOGGING_IN: "logging_in",
  GETTING_USER_INFO: "getting_user_info",
  READY: "ready",
  PREPARING_MESSAGE: "preparing_message",
  SIGNING_MESSAGE: "signing_message",
  DISCONNECTING: "disconnecting",
  DISCONNECTED: "disconnected",
};

const processOwnedTokens = (ownedTokens) => {
  // Go down priority order for each of the user's wallets (Patron always wins, etc)
  var primaryNFTType = null;
  if (ownedTokens && ownedTokens.includes("patron_card")) {
    primaryNFTType = "patron_card";
  } else if (ownedTokens && ownedTokens.includes("exec_card")) {
    primaryNFTType = "exec_card";
  } else if (ownedTokens && ownedTokens.includes("pool_card")) {
    primaryNFTType = "pool_card";
  }
  return primaryNFTType;
};

const processOwnedTokenIDs = (ownedTokenIDs) => {
  // Don't think we need to do any manipulation here
  return ownedTokenIDs;
};

export default {
  STATE: STATE,
  install(vue, options) {
    // Plugin depends on your store
    if (!options || !options.store) {
      throw new Error("Please provide vuex store.");
    }
    const store = options.store;
    if (store.hasModule("nft")) {
      // We're already loaded!
      return;
    }

    // Plugin depends on your Firebase
    if (!options || !options.firebase) {
      throw new Error("Please provide firebase object.");
    }
    const firebase = options.firebase;

    // Register our own vuex module
    store.registerModule("nft", {
      namespaced: true,
      state: {
        ethereumConfig: {},
        ethereumProvider: {},
        error: false,
        connectionState: STATE.LOADING,
        currentSigner: {},
        // user variables
        userAddress: null,
        isEthereumOnlyUser: false,
        // whether to set updates in the main Vuex store for
        // NFT-related variables
        monitoringNFTState: true,
        // NFT state -- both public and "private" mirrors
        _primaryNFTType: null,
        primaryNFTType: null,
        _userNFTs: [],
        userNFTs: [],
        //
      },
      getters: {
        primaryNFTType(state) {
          return state.primaryNFTType;
        },
        userNFTs(state) {
          return state.userNFTs;
        },
        userAddress(state) {
          return state.userAddress;
        },
        isEthereumOnlyUser(state) {
          return state.isEthereumOnlyUser;
        },
        error(state) {
          return state.error;
        },
        connectionState(state) {
          return state.connectionState;
        },
        ethereumProvider(state) {
          return state.ethereumProvider;
        },
        ethereumConfig(state) {
          return state.ethereumConfig;
        },
      },
      mutations: {
        setMonitoringNFTState(state, payload) {
          if (state.monitoringNFTState === false && payload === true) {
            // If we are asking for state again...
            Vue.set(state, "primaryNFTType", state._primaryNFTType);
            Vue.set(state, "userNFTs", state._userNFTs);
          }
          Vue.set(state, "monitoringNFTState", payload);
        },
        setPrimaryNFTType(state, payload) {
          Vue.set(state, "_primaryNFTType", payload.primaryNFTType);
          if (state.monitoringNFTState) {
            Vue.set(state, "primaryNFTType", payload.primaryNFTType);
          }
        },
        setUserNFTs(state, payload) {
          Vue.set(state, "_userNFTs", payload.userNFTs);
          if (state.monitoringNFTState) {
            Vue.set(state, "userNFTs", payload.userNFTs);
          }
        },
        setUserAddress(state, payload) {
          Vue.set(state, "userAddress", payload.userAddress);
        },
        setIsEthereumOnlyUser(state, payload) {
          Vue.set(state, "isEthereumOnlyUser", payload.isEthereumOnlyUser);
        },
        setConnectionState(state, connectionState) {
          Vue.set(state, "connectionState", connectionState);
        },
        setEthereumConfig(state, payload) {
          Vue.set(state, "ethereumConfig", payload.config);
        },
        setEthereumProvider(state, payload) {
          Vue.set(state, "ethereumProvider", payload.provider);
        },
        setCurrentSigner(state, payload) {
          Vue.set(state, "currentSigner", payload);
        },
        setError(state, payload) {
          Vue.set(state, "error", payload.error);
        },
      },
      actions: {
        //
        // start()
        // Called by the client to activate the store
        //
        start({ commit }) {
          return new Promise((resolve, reject) => {
            // Get our Ethereum config from Firebase
            firebase
              .firestore()
              .collection("executive_config")
              .doc("ethereum_signing")
              .get()
              .then((ethereumConfig) => {
                const config = ethereumConfig.data();
                commit("setEthereumConfig", { config });
                if (config.alchemy_rpc_url) {
                  // Setup our premium Alchemy provider for Eth API calls
                  const provider = new ethers.providers.JsonRpcProvider(
                    config.alchemy_rpc_url
                  );
                  commit("setEthereumProvider", { provider });
                }
              })
              .catch((e) => {
                console.log(e);
                reject(e);
              });

            // Check if we are logged in and check if we are logged in
            // with a wallet only
            const user = firebase.auth().currentUser;
            if (user) {
              user
                .getIdTokenResult()
                .then((token) => {
                  const isEthereumOnlyUser =
                    token && token.claims.is_ethereum_user ? true : false;
                  commit("setIsEthereumOnlyUser", { isEthereumOnlyUser });
                })
                .catch((e) => {
                  reject(e);
                });

              firebase
                .firestore()
                .collection("executive_addresses")
                .where("uid", "==", user.uid)
                .get()
                .then((querySnapshot) => {
                  if (!querySnapshot || querySnapshot.size === 0) {
                    commit("setConnectionState", STATE.DISCONNECTED);
                    // All done -- resolve the promise
                    return resolve();
                  }
                  // There should only be one connected address at a time
                  const doc = querySnapshot.docs[0];
                  var data = doc.data();

                  // set the NFT type that we own

                  const primaryNFTType = processOwnedTokens(data.ownedTokens);
                  commit("setPrimaryNFTType", { primaryNFTType });
                  const userNFTs = processOwnedTokenIDs(data.ownedTokenIDs);
                  commit("setUserNFTs", { userNFTs });
                  const userAddress = doc.ref.id;
                  commit("setUserAddress", { userAddress });

                  console.log("** READY LOAD: " + primaryNFTType);

                  const connectionState = primaryNFTType
                    ? STATE.READY
                    : STATE.DISCONNECTED;
                  commit("setConnectionState", connectionState);
                  // All done -- resolve the promise
                  resolve();
                })
                .catch((error) => {
                  commit("setConnectionState", STATE.DISCONNECTED);
                  // There was an error -- reject the promise
                  reject(error);
                });
            } else {
              // All done (no user) -- resolve the promise
              commit("setConnectionState", STATE.DISCONNECTED);
              resolve();
            }
          });
        },
        //
        // State update functions
        //
        updateStateWithUserAddress({ dispatch, commit }, payload) {
          const userAddress = payload.userAddress;
          commit("setUserAddress", { userAddress });
          if (!userAddress) {
            dispatch("updateStateWithTokens", {
              ownedTokens: [],
              ownedTokenIDs: [],
              userAddress: false,
            });
            return;
          }
          // Now that we have the user's address, update
          // their owned tokens from Firebase
          const tokensForAddress = firebase
            .app()
            .functions()
            .httpsCallable("tokensForAddress");
          commit("setConnectionState", STATE.GETTING_USER_INFO);
          tokensForAddress({ ethereumAddress: userAddress })
            .then((result) => {
              const data = (result && result.data) || {};
              dispatch("updateStateWithTokens", {
                ownedTokens: data.ownedTokens,
                ownedTokenIDs: data.ownedTokenIDs,
                userAddress: userAddress,
              });
            })
            .catch((err) => {
              dispatch("updateStateWithTokens", {
                ownedTokens: [],
                ownedTokenIDs: [],
                userAddress: false,
              });
            });
        },
        updateStateWithTokens(
          { commit },
          { ownedTokens, ownedTokenIDs, userAddress }
        ) {
          // Get our primary NFT type (obtained from our Firebase and direct from smart contract)
          const primaryNFTType = processOwnedTokens(ownedTokens);
          // Get all our owned tokens IDs (obtained from the Zora GraphQL instance)
          const userNFTs = processOwnedTokenIDs(ownedTokenIDs);

          commit("setPrimaryNFTType", { primaryNFTType });
          commit("setUserNFTs", { userNFTs });
          commit("setUserAddress", { userAddress });
          commit("setConnectionState", STATE.READY);
        },
        //
        // Sign a validation message (agreed upon with the server)
        // to verify the signer is who they say they are
        //
        async signPoolsuiteMessage({ commit, state }) {
          const currentSigner = state.currentSigner;
          if (!currentSigner) {
            throw new Error("No current signer");
          }

          // Prepares the signing message from the Poolsuite servers
          const prepareSigningMessage = firebase
            .app()
            .functions()
            .httpsCallable("prepareSigningMessage");
          commit("setConnectionState", STATE.PREPARING_MESSAGE);
          const result = await prepareSigningMessage({
            ethereumAddress: state.userAddress,
          });
          if (!result || !result.data || !result.data.message) {
            throw new Error("Error while preparing signing message.");
          }

          // Now ask the client to sign the message (so we can use the signature for anything that needs to
          // authorize us)
          const signingMessage = result.data.message;
          if (currentSigner.type === "walletConnect") {
            // Construct the signing message
            const messageParameters = [
              ethers.utils.toUtf8Bytes(signingMessage),
              state.userAddress,
            ];
            // Return the signature back to caller
            commit("setConnectionState", STATE.SIGNING_MESSAGE);

            let signature, error;
            try {
              signature = await currentSigner.signer.signPersonalMessage(
                messageParameters
              );
            } catch (e) {
              error = e;
            }

            commit("setConnectionState", STATE.READY);
            return { signature, error };
          } else if (
            currentSigner.type === "metaMask" ||
            currentSigner.type === "walletLink"
          ) {
            // return the signature back to the client to do whatever it wishes
            commit("setConnectionState", STATE.SIGNING_MESSAGE);

            let signature, error;
            try {
              signature = await currentSigner.signer.signMessage(
                signingMessage
              );
            } catch (e) {
              error = e;
            }
            commit("setConnectionState", STATE.READY);
            // return the signature back to the client to do whatever it wishes
            return { signature, error };
          }
        },
        //
        // Reset
        //
        async reset({ commit }) {
          commit("setPrimaryNFTType", { primaryNFTType: null });
          commit("setUserNFTs", { userNFTs: null });
          commit("setConnectionState", STATE.DISCONNECTED);
        },
        //
        // NFT Handling
        //
        // connectNFT()
        // Opens the wallet of choice and begins the connection process
        // By the end, the client will have the user's address and Poolsuite NFTs
        // in their ownership
        // Params:
        //   - type: what wallet are you connecting with?
        //           available options: walletMink, metaMask, walletConnect
        //
        async connectNFT({ state, commit, dispatch }, type) {
          // Get the signing message from our Ethereum config
          const ethereumConfig = state.ethereumConfig;
          const expectedNetworkName = ethereumConfig.network_name
            ? ethereumConfig.network_name
            : "correct";
          const expectedChainID = ethereumConfig.chain_id;
          const rpcURL = ethereumConfig.alchemy_rpc_url;

          if (type === "walletConnect") {
            const connector = new WalletConnect({
              bridge: "https://bridge.walletconnect.org",
            });

            // Kill any old sessions just to keep clean
            if (connector.connected) {
              await connector.killSession();
            }

            commit("setError", { error: null });
            commit("setConnectionState", STATE.LOGGING_IN);

            // Create a new session
            await connector.createSession();

            QRCodeModal.open(connector.uri, () => {
              commit("setConnectionState", STATE.DISCONNECTED);
              commit("setError", {
                error:
                  "Could not get your wallet address. Did you connect your account?",
              });
              return;
            });

            // Subscribe to connection events
            connector.on("connect", (error, payload) => {
              QRCodeModal.close();

              if (error) {
                commit("setConnectionState", STATE.DISCONNECTED);
                commit("setError", { error: error.message });
                connector.killSession();
                return;
              }

              // console.log(payload);
              // Get the account (address) and chain data
              const { accounts, chainId } = payload.params[0];
              // Check that we're on the correct network
              if (chainId !== expectedChainID) {
                const error = `Please make sure you are connected to the ${expectedNetworkName} network.`;
                commit("setConnectionState", STATE.DISCONNECTED);
                commit("setError", { error });
                connector.killSession();
                return;
              }
              commit("setCurrentSigner", { type: type, signer: connector });
              dispatch("updateStateWithUserAddress", {
                userAddress: accounts[0],
              });
            });
          } else if (type === "metaMask" || type === "walletLink") {
            var ethereum;
            if (type === "metaMask") {
              ethereum = window.ethereum;
              if (!ethereum) {
                window.open("https://metamask.io/");
                return;
              }
            } else if (type === "walletLink") {
              // Initialize WalletLink
              const walletLink = new WalletLink({
                appName: "Poolsuite",
                appLogoUrl: "@/assets/logo.png",
              });
              ethereum = walletLink.makeWeb3Provider(rpcURL, expectedChainID);
              if (!ethereum) {
                window.open("https://walletlink.org/");
                return;
              }
            }

            const provider = new ethers.providers.Web3Provider(ethereum);
            commit("setConnectionState", STATE.REQUESTING_WALLET);
            provider
              .send("eth_requestAccounts", [])
              .then(async () => {
                const network = await provider.getNetwork();
                if (
                  network.name !== expectedNetworkName ||
                  network.chainId !== expectedChainID
                ) {
                  const error = `Please make sure you are connected to the ${expectedNetworkName} network.`;
                  commit("setConnectionState", STATE.DISCONNECTED);
                  commit("setError", { error });
                  return;
                }

                commit("setError", { error: null });
                commit("setConnectionState", STATE.LOGGING_IN);

                const signer = provider.getSigner();
                const userAddress = await signer.getAddress();
                commit("setCurrentSigner", { type: type, signer: signer });
                dispatch("updateStateWithUserAddress", {
                  userAddress: userAddress,
                });
              })
              .catch((e) => {
                console.log(e);
                const error = `Could not get your wallet address. Did you connect your account?`;
                commit("setConnectionState", STATE.DISCONNECTED);
                commit("setError", { error });
                return;
              });
          }
        },
        //
        // disconnectNFT()
        // Decouples token ownership from the user account
        //
        async disconnectNFT({ commit }) {
          commit("setConnectionState", STATE.DISCONNECTING);
          const disconnectWalletFromUser = firebase
            .app()
            .functions()
            .httpsCallable("disconnectWalletFromUser");
          disconnectWalletFromUser()
            .then(() => {
              commit("setPrimaryNFTType", { primaryNFTType: null });
              commit("setUserNFTs", { userNFTs: null });
              commit("setConnectionState", STATE.DISCONNECTED);
            })
            .catch((error) => {
              // Likely still connected due to error
              commit("setConnectionState", STATE.READY);
              commit("setError", { error });
              console.log(error);
            });
        },
      },
    });
  },
};
